/**
 * PursuitChatLauncher — floating FAB visible on every pursuit screen.
 *
 * Detects the current URL via `useLocation`. When the path matches
 * `/<workspace>/pursuit/<pursuitId>/*`, renders a fixed-position,
 * icon-only FAB bottom-right. Clicking it expands a small menu with
 * "Ask this Pursuit" (opens the full PursuitChatDrawer, unchanged) and
 * "Feedback" (opens a lightweight PursuitFeedbackPanel).
 *
 * v3.18.18 — User asked: today the chatbot only lives on the Knowledge
 * Base page; while inside a pursuit, the user has to navigate away to
 * ask anything. This launcher makes the chat reachable in one click
 * from any pursuit screen.
 *
 * v3.20.x — Original trigger was a small pill, easy to miss. Redesigned
 * as a bigger icon FAB (inspired by the sibling wmtai project's "Ask Big
 * Dog" widget) that expands into a two-option menu, and added a
 * lightweight Feedback affordance alongside it.
 */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircleQuestion, MessageSquarePlus } from 'lucide-react';
import PursuitChatDrawer from './PursuitChatDrawer';
import PursuitFeedbackPanel from './PursuitFeedbackPanel';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// /<workspace>/pursuit/<pursuitId>/<screen>
const PURSUIT_PATH_RE = /^\/[^/]+\/pursuit\/([^/]+)(?:\/|$)/;

export default function PursuitChatLauncher() {
  const location = useLocation();
  const { getAuthHeaders } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [pursuitName, setPursuitName] = useState(null);

  const match = location.pathname.match(PURSUIT_PATH_RE);
  const pursuitId = match ? match[1] : null;

  // Close everything if user navigates out of pursuit context
  useEffect(() => {
    if (!pursuitId) {
      setMenuOpen(false);
      setOpen(false);
      setFeedbackOpen(false);
    }
  }, [pursuitId]);

  // Fetch pursuit name for the drawer header
  useEffect(() => {
    let cancelled = false;
    if (!pursuitId) {
      setPursuitName(null);
      return undefined;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
          headers: getAuthHeaders(),
        });
        if (!res.ok) return;
        const body = await res.json();
        if (!cancelled) {
          setPursuitName(body.opportunity_name || body.title || null);
        }
      } catch (e) {
        /* non-blocking */
      }
    })();
    return () => { cancelled = true; };
  }, [pursuitId, getAuthHeaders]);

  if (!pursuitId) return null;

  return (
    <>
      {menuOpen && (
        <button
          type="button"
          aria-label="Close menu"
          className="fixed inset-0 z-40 cursor-default"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {menuOpen && (
        <div
          data-testid="pursuit-launcher-menu"
          className="fixed bottom-24 right-6 z-50 w-64 bg-white rounded-lg shadow-2xl border border-slate-200 overflow-hidden"
        >
          <button
            type="button"
            onClick={() => { setOpen(true); setMenuOpen(false); }}
            data-testid="pursuit-launcher-menu-chat"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-800 hover:bg-slate-50 transition-colors text-left"
          >
            <MessageCircleQuestion className="w-4 h-4 text-cyan-700 shrink-0" />
            <span>
              <span className="block font-medium">Ask this Pursuit</span>
              <span className="block text-xs text-slate-400">AI Q&amp;A grounded in this pursuit's docs</span>
            </span>
          </button>
          <button
            type="button"
            onClick={() => { setFeedbackOpen(true); setMenuOpen(false); }}
            data-testid="pursuit-launcher-menu-feedback"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-800 hover:bg-slate-50 transition-colors text-left border-t border-slate-100"
          >
            <MessageSquarePlus className="w-4 h-4 text-cyan-700 shrink-0" />
            <span>
              <span className="block font-medium">Feedback</span>
              <span className="block text-xs text-slate-400">Note a bug, idea, or concern</span>
            </span>
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        title="Ask this Pursuit / Feedback"
        data-testid="pursuit-chat-launcher-btn"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 flex items-center justify-center bg-cyan-700 hover:bg-cyan-800 text-white shadow-lg hover:shadow-xl rounded-full transition-all hover:scale-105"
      >
        <MessageCircleQuestion className="w-6 h-6" />
      </button>

      <PursuitChatDrawer
        open={open}
        onOpenChange={setOpen}
        pursuitId={pursuitId}
        pursuitName={pursuitName}
      />
      <PursuitFeedbackPanel
        open={feedbackOpen}
        onOpenChange={setFeedbackOpen}
        pursuitId={pursuitId}
      />
    </>
  );
}
